import React, { useRef, useEffect, useState, useId } from 'react';

// ─── Icons ────────────────────────────────────────────────────────────────────

const ChevronUpIcon = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const ChevronDownIcon = ({ className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ─── Constants ────────────────────────────────────────────────────────────────

const DURATION_OPEN  = 320;
const DURATION_CLOSE = 260;
const EASING_OPEN    = 'cubic-bezier(0.2, 0, 0, 1)';
const EASING_CLOSE   = 'cubic-bezier(0.4, 0, 1, 1)';

// ─── Component ────────────────────────────────────────────────────────────────

const ExpandableSection = ({
  title,
  icon,
  iconClassName  = '',
  headerClassName = '',
  children,
}) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const raf1Ref  = useRef(null);
  const raf2Ref  = useRef(null);

  const [isExpanded,    setIsExpanded   ] = useState(true);
  const [contentHeight, setContentHeight] = useState('auto');
  const [reducedMotion, setReducedMotion] = useState(false);

  const duration = isExpanded ? DURATION_OPEN  : DURATION_CLOSE;
  const easing   = isExpanded ? EASING_OPEN    : EASING_CLOSE;

  // Reduced-motion preference
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    const update = () => setReducedMotion(!!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // Set height to 'auto' after expand transition ends
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      if (isExpanded) setContentHeight('auto');
    };
    el.addEventListener('transitionend', onEnd);
    return () => el.removeEventListener('transitionend', onEnd);
  }, [isExpanded]);

  // Adjust target height if content changes while animating
  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner || !isExpanded || reducedMotion) return;
    const update = () => {
      if (contentHeight === 'auto') return;
      setContentHeight(`${inner.scrollHeight}px`);
    };
    const ro = new ResizeObserver(update);
    ro.observe(inner);
    update();
    return () => ro.disconnect();
  }, [isExpanded, reducedMotion, contentHeight, children]);

  // Core expand / collapse animation
  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
    if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);

    if (reducedMotion) {
      setContentHeight(isExpanded ? 'auto' : '0px');
      return;
    }

    if (isExpanded) {
      setContentHeight(`${inner.scrollHeight}px`);
    } else {
      const { height } = outer.getBoundingClientRect();
      setContentHeight(`${Math.max(0, Math.round(height))}px`);
      void outer.offsetHeight; // force layout
      raf1Ref.current = requestAnimationFrame(() => {
        raf2Ref.current = requestAnimationFrame(() => setContentHeight('0px'));
      });
    }

    return () => {
      if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
      if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
    };
  }, [isExpanded, reducedMotion]);

  const baseId   = useId();
  const headerId = `${baseId}-header`;
  const regionId = `${baseId}-region`;

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">

      {/* Header */}
      <div
        id={headerId}
        className={`flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 ${headerClassName}`}
      >
        <h3 className="flex items-center gap-2 text-sm sm:text-base font-semibold text-indigo-800 m-0">
          {icon && (
            <span className={`flex-shrink-0 w-4 h-4 sm:w-5 sm:h-5 ${iconClassName}`}>
              {icon}
            </span>
          )}
          {title}
        </h3>

        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          aria-expanded={isExpanded}
          aria-controls={regionId}
          aria-label={isExpanded ? 'סגור חלק' : 'פתח חלק'}
          className="flex-shrink-0 p-1 rounded-full text-indigo-500 hover:text-indigo-700 hover:bg-indigo-50 transition-colors"
        >
          {isExpanded
            ? <ChevronUpIcon   className="w-4 h-4 sm:w-5 sm:h-5" />
            : <ChevronDownIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          }
        </button>
      </div>

      {/* Animated content */}
      <div
        id={regionId}
        role="region"
        aria-labelledby={headerId}
        ref={outerRef}
        className="overflow-hidden"
        style={{
          height:     contentHeight,
          transition: reducedMotion ? 'none' : `height ${duration}ms ${easing}`,
          willChange: reducedMotion ? undefined : 'height',
        }}
      >
        <div ref={innerRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default ExpandableSection;